import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, BarChart2, Brain } from 'lucide-react';
import { Card } from '../ui/Card';
import { ProgressBar } from '../ui/Progress';
import { AppState } from '../../types';
import { MODULES } from '../../data/modules';
import { getAvailableLevelsByGroup, getLevelLabel } from '../../config/levels';
import { GroupId } from '../../config/systemConfig';
interface StatsDrawerProps {
  state: AppState;
  selectedGroup: GroupId;
}
export function StatsDrawer({
  state,
  selectedGroup
}: StatsDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const totalModules = MODULES.length;
  const masteredModules = Object.values(state.progress).filter(p => p.masteryPercentage >= 95).length;
  const startedModules = Object.values(state.progress).filter(p => p.masteryPercentage > 0).length;
  // Calculate average mastery
  const totalMastery = Object.values(state.progress).reduce((sum, p) => sum + p.masteryPercentage, 0);
  const averageMastery = Math.round(totalMastery / totalModules);
  // Get available levels for the SELECTED GROUP only
  const availableLevels = getAvailableLevelsByGroup(selectedGroup);
  return <div className="mb-12" data-id="element-536">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors group" data-id="element-537">
        <div className="flex items-center gap-3" data-id="element-538">
          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-100 transition-colors" data-id="element-539">
            <BarChart2 className="w-5 h-5" data-id="element-540" />
          </div>
          <div className="text-left" data-id="element-541">
            <h3 className="font-bold text-slate-900" data-id="element-542">Jouw Statistieken</h3>
            <p className="text-sm text-slate-500" data-id="element-543">
              Groep {selectedGroup} • {startedModules} van {totalModules} vakken
              gestart • {averageMastery}% gemiddeld
            </p>
          </div>
        </div>
        <div className={`p-2 rounded-full bg-slate-100 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-544">
          <ChevronDown className="w-5 h-5" data-id="element-545" />
        </div>
      </button>

      <AnimatePresence data-id="element-546">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0,
        marginTop: 0
      }} animate={{
        height: 'auto',
        opacity: 1,
        marginTop: 16
      }} exit={{
        height: 0,
        opacity: 0,
        marginTop: 0
      }} className="overflow-hidden" data-id="element-547">
            <Card className="p-6 bg-slate-50 border-slate-200" data-id="element-548">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-549">
                {/* Module Breakdown */}
                <div data-id="element-550">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-551">
                    <Brain className="w-4 h-4 text-indigo-500" data-id="element-552" />
                    Voortgang per Vak
                  </h4>
                  <div className="space-y-4" data-id="element-553">
                    {MODULES.map(module => {
                  const progress = state.progress[module.id];
                  return <div key={module.id} data-id="element-554">
                          <div className="flex justify-between text-sm mb-1" data-id="element-555">
                            <span className="font-medium text-slate-700" data-id="element-556">
                              {module.title}
                            </span>
                            <span className="font-bold text-slate-900" data-id="element-557">
                              {progress.masteryPercentage}%
                            </span>
                          </div>
                          <ProgressBar value={progress.masteryPercentage} height={6} color={progress.masteryPercentage >= 95 ? 'bg-emerald-500' : progress.masteryPercentage >= 60 ? 'bg-indigo-500' : 'bg-slate-300'} data-id="element-558" />
                        </div>;
                })}
                  </div>
                </div>

                {/* Difficulty Breakdown - Filtered by selected group */}
                <div data-id="element-559">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-560">
                    <BarChart2 className="w-4 h-4 text-indigo-500" data-id="element-561" />
                    Beheersing per Niveau (Groep {selectedGroup})
                  </h4>
                  <div className="space-y-4" data-id="element-562">
                    {availableLevels.map(level => {
                  const levelTotal = Object.values(state.progress).reduce((sum, p) => {
                    return sum + (p.masteryByDifficulty?.[level.id] || 0);
                  }, 0);
                  const levelAverage = Math.round(levelTotal / totalModules);
                  return <div key={level.id} data-id="element-563">
                          <div className="flex justify-between text-sm mb-1" data-id="element-564">
                            <span className="font-medium text-slate-700" data-id="element-565">
                              {level.title}
                            </span>
                            <span className="font-bold text-slate-900" data-id="element-566">
                              {levelAverage}%
                            </span>
                          </div>
                          <ProgressBar value={levelAverage} height={6} color={levelAverage >= 95 ? 'bg-emerald-500' : levelAverage >= 60 ? 'bg-indigo-500' : 'bg-slate-300'} data-id="element-567" />
                        </div>;
                })}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>}
      </AnimatePresence>
    </div>;
}